import { useId, useState } from 'react';
import {
  CompoundEntityRef,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_PROVIDES_API,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { Link } from '@backstage/core-components';
import {
  EntityRefLink,
  getEntityRelations,
  useEntity,
} from '@backstage/plugin-catalog-react';
import { Box, Typography, makeStyles } from '@material-ui/core';

const ROW = 44;
const GAP = 8;
const STEP = ROW + GAP;
const MAX_ROWS = 8;
const GUTTER = 72;

const useStyles = makeStyles(theme => ({
  panel: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    background: theme.palette.background.paper,
    padding: theme.spacing(3),
  },
  flow: {
    display: 'grid',
    gridTemplateColumns: `minmax(0, 1fr) ${GUTTER}px minmax(140px, 0.7fr) ${GUTTER}px minmax(0, 1fr)`,
    alignItems: 'center',
  },
  column: { display: 'flex', flexDirection: 'column', gap: GAP },
  node: {
    height: ROW,
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    cursor: 'default',
  },
  more: { color: theme.palette.text.secondary, cursor: 'pointer' },
  center: {
    border: `2px solid ${theme.palette.primary.main}`,
    fontWeight: 600,
    justifyContent: 'center',
    textAlign: 'center',
  },
  heading: {
    fontSize: 11,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  events: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
}));

type Refs = CompoundEntityRef[];

/** Curved arrows between a column of nodes and the service in the middle. */
const Gutter = ({
  rows,
  height,
  pointsAtService,
}: {
  rows: number;
  height: number;
  pointsAtService: boolean;
}) => {
  const id = useId();
  const cy = height / 2;
  const top = (height - (rows * STEP - GAP)) / 2;
  const ys = Array.from({ length: rows }, (_, i) => top + i * STEP + ROW / 2);
  return (
    <svg width={GUTTER} height={height} style={{ color: '#9aa0ad' }}>
      <defs>
        <marker
          id={id}
          markerWidth="8"
          markerHeight="8"
          refX="6"
          refY="3"
          orient="auto"
        >
          <path d="M0,0 L6,3 L0,6 z" fill="currentColor" />
        </marker>
      </defs>
      {ys.map(y => (
        <path
          key={y}
          d={
            pointsAtService
              ? `M0,${y} C36,${y} 36,${cy} ${GUTTER - 2},${cy}`
              : `M2,${cy} C36,${cy} 36,${y} ${GUTTER},${y}`
          }
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          markerEnd={`url(#${id})`}
        />
      ))}
    </svg>
  );
};

/** One side of the diagram: a heading and its nodes, collapsed past a few. */
const useSide = (refs: Refs) => {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? refs : refs.slice(0, MAX_ROWS);
  const hidden = refs.length - shown.length;
  const rows = Math.max(shown.length + (hidden > 0 || expanded ? 1 : 0), 0);
  return { shown, hidden, expanded, setExpanded, rows };
};

const Side = ({
  title,
  refs,
  side,
}: {
  title: string;
  refs: Refs;
  side: ReturnType<typeof useSide>;
}) => {
  const classes = useStyles();
  const { shown, hidden, expanded, setExpanded } = side;
  return (
    <Box className={classes.column} justifyContent="center">
      {shown.map(ref => (
        <Box key={stringifyEntityRef(ref)} className={classes.node}>
          <EntityRefLink entityRef={ref} />
        </Box>
      ))}
      {refs.length === 0 && (
        <Box className={`${classes.node} ${classes.more}`}>
          No {title.toLowerCase()} recorded
        </Box>
      )}
      {(hidden > 0 || (expanded && refs.length > MAX_ROWS)) && (
        <Box
          className={`${classes.node} ${classes.more}`}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? 'Show fewer' : `+ ${hidden} more`}
        </Box>
      )}
    </Box>
  );
};

/** APIs and events the service provides or consumes, as a row of links. */
const Events = ({ title, refs }: { title: string; refs: Refs }) => {
  const classes = useStyles();
  return (
    <Box>
      <Typography className={classes.heading}>
        {title} ({refs.length})
      </Typography>
      <Box className={classes.events}>
        {refs.length === 0 && (
          <Typography variant="body2" color="textSecondary">
            None recorded
          </Typography>
        )}
        {refs.map(ref => (
          <Box key={stringifyEntityRef(ref)} className={classes.node}>
            <EntityRefLink entityRef={ref} />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

/** Who calls this service and what it calls, drawn with arrows, then its events and APIs. */
export const RelationsFlow = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const callers = getEntityRelations(entity, RELATION_DEPENDENCY_OF);
  const calls = getEntityRelations(entity, RELATION_DEPENDS_ON);
  const left = useSide(callers);
  const right = useSide(calls);
  const rows = Math.max(left.rows, right.rows, 1);
  const height = rows * STEP - GAP;
  const graphLink = `/catalog-graph?rootEntityRefs[]=${encodeURIComponent(stringifyEntityRef(entity))}`;

  return (
    <Box className={classes.panel}>
      <Box className={classes.flow} mb={1}>
        <Typography className={classes.heading}>
          Called by ({callers.length})
        </Typography>
        <span />
        <span />
        <span />
        <Typography className={classes.heading} align="right">
          Calls ({calls.length})
        </Typography>
      </Box>
      <Box className={classes.flow} style={{ minHeight: height }}>
        <Side title="Callers" refs={callers} side={left} />
        <Gutter rows={left.shown.length} height={height} pointsAtService />
        <Box
          className={`${classes.node} ${classes.center}`}
          style={{ height: ROW }}
        >
          {entity.metadata.title ?? entity.metadata.name}
        </Box>
        <Gutter
          rows={right.shown.length}
          height={height}
          pointsAtService={false}
        />
        <Side title="Calls" refs={calls} side={right} />
      </Box>
      <Box mt={3} display="grid" style={{ gap: 16 }}>
        <Events
          title="Provides"
          refs={getEntityRelations(entity, RELATION_PROVIDES_API)}
        />
        <Events
          title="Consumes"
          refs={getEntityRelations(entity, RELATION_CONSUMES_API)}
        />
      </Box>
      <Box mt={1} textAlign="right">
        <Link to={graphLink}>Open the full graph →</Link>
      </Box>
    </Box>
  );
};
